import {useMemo} from 'react';
import {ModelDTO} from 'hemwb-api';
import {useModelFlatHierarchy} from '../../../../store/modelLinks/hooks';

/**
 * Hook to get the family tree for a model (parent + all descendants)
 * Returns a nested structure suitable for Ant Design Table with children property
 *
 * @param modelUuid - UUID of any model in the family (can be root, child, or grandchild)
 * @returns Object with familyTree array and parentModel (if current model is a child)
 */
export const useModelFamily = (modelUuid: string | undefined) => {
  // Get flat hierarchy from existing hook (already fetches from /model/:uuid/links/all)
  const flatHierarchy = useModelFlatHierarchy(modelUuid);

  // Transform flat hierarchy into nested structure for table and find parent
  const result = useMemo(() => {
    if (!flatHierarchy || flatHierarchy.length === 0) {
      return undefined;
    }

    // Build a map for quick lookup
    const modelMap: Record<string, ModelDTO & {children?: ModelDTO[]}> = {};

    // First pass: Create all nodes (skip inactive/restricted models with no name)
    flatHierarchy.forEach(({model}) => {
      if (model && 'name' in model && model.name) {
        modelMap[model.uuid] = {
          ...model,
          children: [],
        } as ModelDTO & {children?: ModelDTO[]};
      }
    });

    // Second pass: Build parent-child relationships and calculate levels
    const roots: (ModelDTO & {children?: ModelDTO[]; isChild?: boolean; level?: number})[] = [];

    // Helper to calculate level recursively
    const calculateLevel = (uuid: string, visitedSet = new Set<string>()): number => {
      if (visitedSet.has(uuid)) return 0; // Prevent circular references
      visitedSet.add(uuid);

      const model = modelMap[uuid];
      if (!model) return 0;

      const parentId = (model as any).metadata.tree_parent_id;
      if (!parentId || !modelMap[parentId]) {
        return 0; // Root level
      }

      return calculateLevel(parentId, visitedSet) + 1;
    };

    flatHierarchy.forEach(({model}) => {
      if (model && 'name' in model && model.name) {
        const parentId = model.metadata.tree_parent_id;
        const level = calculateLevel(model.uuid);

        // Set level on the node
        (modelMap[model.uuid] as any).level = level;

        if (parentId && modelMap[parentId] && modelMap[parentId].name) {
          // This is a child, add to parent's children array
          // Mark as child but keep reference to preserve nested children
          (modelMap[model.uuid] as any).isChild = true;
          modelMap[parentId].children!.push(modelMap[model.uuid]);
        } else {
          // This is a root node
          roots.push(modelMap[model.uuid]);
        }
      }
    });

    // Clean up empty children arrays
    Object.values(modelMap).forEach((node) => {
      if (node.children && node.children.length === 0) {
        delete node.children;
      }
    });

    // Find parent model if current model is a child
    const currentModel = flatHierarchy.find(({model}) => model?.uuid === modelUuid)?.model;
    const parentId = currentModel?.metadata.tree_parent_id;
    const parentModel = parentId ? modelMap[parentId] : undefined;

    return {
      familyTree: roots,
      parentModel,
    };
  }, [flatHierarchy, modelUuid]);

  return result;
};
